import { useEffect, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Boxes,
  Check,
  Copy,
  Database,
  KeyRound,
  Layers,
  Network,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";
import { IntegrationLogo } from "@/components/integrations/logo";
import { HighlightedCode } from "@/components/site/highlighted-code";
import { INTEGRATIONS } from "@/data/integrations";
import { cn } from "@/lib/utils";

/**
 * Reusable animated illustrations for the documentation pages. Each
 * visual is self-contained — pop it at the top of a doc page (above the
 * prose body) or inline alongside a numbered step.
 */

/**
 * Validate at module load that every slug referenced by a doc visual
 * exists in the runtime REGISTRY. Throws on miss so we never silently
 * render a letter-tile fallback in the docs.
 */
function fromRegistry(slug: string): string {
  if (!INTEGRATIONS.some((i) => i.slug === slug)) {
    throw new Error(`docs/visuals: slug "${slug}" not in REGISTRY`);
  }
  return slug;
}

// ── Shell ──────────────────────────────────────────────────────────

interface DocVisualProps {
  children: ReactNode;
  /** Pill caption that overlays the top-left corner. */
  label?: string;
  className?: string;
}

export function DocVisual({ children, label, className }: DocVisualProps) {
  return (
    <div
      className={cn(
        "not-prose relative my-8 overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-card to-card/40 p-6 shadow-sm sm:p-8",
        className,
      )}
    >
      {label && (
        <span className="absolute left-4 top-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      )}
      <div className="relative">{children}</div>
    </div>
  );
}

/** A boxed micro-visual you can drop next to a numbered step. */
export function StepVisual({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "not-prose relative my-4 flex h-[180px] items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-background/40 p-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── Architecture (used by /docs intro) ────────────────────────────

export function ArchitectureVisual() {
  const reduce = useReducedMotion();
  const items = [
    { slug: fromRegistry("gmail"),  name: "Gmail" },
    { slug: fromRegistry("github"), name: "GitHub" },
    { slug: fromRegistry("slack"),  name: "Slack" },
    { slug: fromRegistry("notion"), name: "Notion" },
  ];
  const agents = [
    { slug: fromRegistry("anthropic_administrator"), name: "Claude" },
    { slug: fromRegistry("openai"),                  name: "ChatGPT" },
    { slug: fromRegistry("perplexityai"),            name: "Perplexity" },
    { slug: fromRegistry("replicate"),               name: "Replicate" },
  ];
  return (
    <DocVisual label="System overview">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-6">
        <div className="space-y-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Tools
          </p>
          {items.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={reduce ? false : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
              className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-2 py-1.5 text-[12px]"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <IntegrationLogo slug={p.slug} name={p.name} size={16} />
              </span>
              {p.name}
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={reduce ? false : { scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center gap-2"
        >
          <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500/20 via-fuchsia-500/20 to-teal-400/20 ring-1 ring-foreground/10">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-foreground text-xs font-extrabold text-background">
              mk
            </div>
          </div>
          <p className="text-center text-[11px] font-semibold">MemKernel</p>
          <p className="text-center text-[9px] text-muted-foreground">
            Tools · Memory · Vectors · Skills · Auth
          </p>
        </motion.div>
        <div className="space-y-2">
          <p className="mb-1 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Agents
          </p>
          {agents.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={reduce ? false : { opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.06 }}
              className="flex flex-row-reverse items-center gap-2 rounded-md border border-border/60 bg-card px-2 py-1.5 text-right text-[12px]"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <IntegrationLogo slug={p.slug} name={p.name} size={16} />
              </span>
              {p.name}
            </motion.div>
          ))}
        </div>
      </div>
    </DocVisual>
  );
}

// ── Project hierarchy ─────────────────────────────────────────────

export function ProjectHierarchyVisual() {
  return (
    <DocVisual label="Project anatomy">
      <div className="flex flex-col items-center gap-3">
        <span className="rounded-md border border-foreground/15 bg-card px-3 py-1.5 text-[12px] font-semibold">
          User: you@example.com
        </span>
        <ArrowDown />
        <span className="rounded-md border border-foreground/15 bg-card px-3 py-1.5 text-[12px] font-semibold">
          Project: personal-assistant
        </span>
        <ArrowDown />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {[
            { icon: Workflow,    label: "Tools" },
            { icon: Network,     label: "MCP" },
            { icon: Database,    label: "Memory" },
            { icon: Sparkles,    label: "Vectors" },
            { icon: ShieldCheck, label: "Perms" },
          ].map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.04 }}
              className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background/60 px-2 py-1.5 text-[11px]"
            >
              <m.icon className="h-3 w-3 text-foreground/70" />
              {m.label}
            </motion.div>
          ))}
        </div>
      </div>
    </DocVisual>
  );
}

function ArrowDown() {
  return (
    <span className="text-muted-foreground" aria-hidden>
      ↓
    </span>
  );
}

// ── Multi-protocol export ────────────────────────────────────────

export function ToolExportVisual() {
  const protocols = [
    { name: "MCP",        color: "#A855F7" },
    { name: "OpenAI fn",  color: "#10A37F" },
    { name: "Anthropic",  color: "#D97757" },
    { name: "LangChain",  color: "#6366F1" },
    { name: "Mastra",     color: "#0EA5E9" },
    { name: "REST",       color: "#F59E0B" },
  ];
  const reduce = useReducedMotion();
  return (
    <DocVisual label="Multi-protocol export">
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-2 rounded-lg border border-foreground/15 bg-card px-3 py-2">
          <span className="flex h-5 w-5 items-center justify-center rounded bg-white p-0.5 ring-1 ring-black/5">
            <IntegrationLogo slug="gmail" name="Gmail" size={14} />
          </span>
          <code className="font-mono text-[12px] font-medium">gmail.search</code>
        </div>
        <span className="text-muted-foreground" aria-hidden>↓ exports to</span>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {protocols.map((p, i) => (
            <motion.span
              key={p.name}
              initial={reduce ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className="rounded-full border px-2 py-0.5 text-[11px] font-semibold"
              style={{
                background: `${p.color}1f`,
                borderColor: `${p.color}55`,
                color: p.color,
              }}
            >
              {p.name}
            </motion.span>
          ))}
        </div>
      </div>
    </DocVisual>
  );
}

// ── MCP URL / Claude Desktop config ──────────────────────────────

export function McpUrlVisual() {
  const [copied, setCopied] = useState(false);
  return (
    <DocVisual label="MCP endpoint">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-foreground/10 bg-zinc-950 px-3 py-2 shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          <code className="flex-1 truncate font-mono text-[12px] text-zinc-200">
            https://mcp.memkernel.com/p/personal-assistant
          </code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(
                "https://mcp.memkernel.com/p/personal-assistant",
              );
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1400);
            }}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            aria-label="Copy MCP URL"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            { slug: fromRegistry("anthropic_administrator"), name: "Claude Desktop" },
            { slug: fromRegistry("gemini"),                  name: "Cursor" },
            { slug: fromRegistry("openai"),                  name: "Open WebUI" },
            { slug: fromRegistry("github"),                  name: "Copilot" },
          ].map((c) => (
            <div
              key={c.slug}
              className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-2 py-1.5 text-[11px]"
            >
              <IntegrationLogo slug={c.slug} name={c.name} size={14} />
              <span className="truncate">{c.name}</span>
              <Check className="ml-auto h-3 w-3 text-emerald-400" />
            </div>
          ))}
        </div>
      </div>
    </DocVisual>
  );
}

// ── Memory recall ────────────────────────────────────────────────

export function MemoryRecallVisual() {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState(reduce ? 3 : 0);
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setPhase((p) => (p + 1) % 4), 1300);
    return () => window.clearInterval(id);
  }, [reduce]);
  const hits = [
    "user.preferences.tone: terse",
    "user.tz: Europe/Berlin",
    "session.thread: Q4 planning",
  ];
  return (
    <DocVisual label="Memory recall">
      <div className="flex flex-col gap-3">
        <div className="self-end max-w-[80%] rounded-2xl rounded-br-md bg-foreground/10 px-3 py-2 text-[12px]">
          "Brief me on Q4 planning."
        </div>
        <div className="space-y-1">
          {hits.map((h, i) => (
            <motion.div
              key={h}
              initial={false}
              animate={{
                opacity: phase >= 1 + i ? 1 : 0,
                x: phase >= 1 + i ? 0 : -6,
              }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1.5 text-[11px]"
            >
              <Sparkles className="h-3 w-3 shrink-0 text-emerald-400" />
              <span className="truncate font-mono">{h}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </DocVisual>
  );
}

// ── Vector recall ────────────────────────────────────────────────

export function VectorRecallVisual() {
  const reduce = useReducedMotion();
  const results = [
    { id: "doc-42", score: "0.91", title: "memkernel architecture spec" },
    { id: "doc-71", score: "0.87", title: "MCP transport options"       },
    { id: "doc-13", score: "0.82", title: "tool registry guide"         },
    { id: "doc-08", score: "0.78", title: "OAuth scope reference"       },
  ];
  return (
    <DocVisual label="Semantic search">
      <div className="space-y-3">
        <div className="rounded-md border border-foreground/10 bg-zinc-950 px-3 py-2">
          <code className="font-mono text-[12px] text-zinc-200">
            ? "how does memkernel route mcp calls"
          </code>
        </div>
        <div className="space-y-1.5">
          {results.map((r, i) => (
            <motion.div
              key={r.id}
              initial={reduce ? false : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="flex items-center gap-3 rounded-md border border-border/60 bg-card px-3 py-1.5 text-[11px]"
            >
              <span className="font-mono text-fuchsia-300">{r.score}</span>
              <span className="flex-1 truncate">{r.title}</span>
              <span className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
                {r.id}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </DocVisual>
  );
}

// ── Permission matrix ────────────────────────────────────────────

export function PermissionMatrixVisual() {
  const reduce = useReducedMotion();
  const cols = [
    { slug: fromRegistry("anthropic_administrator"), name: "Claude"  },
    { slug: fromRegistry("openai"),                  name: "ChatGPT" },
    { slug: fromRegistry("github"),                  name: "Copilot" },
  ];
  const rows = [
    { slug: fromRegistry("gmail"),       name: "Gmail",  allow: [true,  false, false] },
    { slug: fromRegistry("github"),      name: "GitHub", allow: [false, true,  true ] },
    { slug: fromRegistry("googledrive"), name: "Drive",  allow: [true,  true,  false] },
    { slug: fromRegistry("stripe"),      name: "Stripe", allow: [false, false, false] },
  ];
  return (
    <DocVisual label="Permissions">
      <div className="overflow-x-auto">
        <div className="grid grid-cols-[auto_repeat(3,minmax(60px,1fr))] gap-2 text-[12px]">
          <div />
          {cols.map((c) => (
            <div key={c.slug} className="flex justify-center">
              <span className="flex h-6 w-6 items-center justify-center">
                <IntegrationLogo slug={c.slug} name={c.name} size={20} />
              </span>
            </div>
          ))}
          {rows.map((row, ri) => (
            <div key={row.slug} className="contents">
              <div className="flex items-center gap-1.5 text-[11px] font-medium">
                <span className="flex h-5 w-5 items-center justify-center">
                  <IntegrationLogo slug={row.slug} name={row.name} size={16} />
                </span>
                <span>{row.name}</span>
              </div>
              {row.allow.map((allowed, ci) => (
                <motion.div
                  key={ci}
                  initial={reduce ? false : { scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.15 + (ri * 3 + ci) * 0.04 }}
                  className={cn(
                    "flex h-7 items-center justify-center rounded-md text-[12px] font-bold",
                    allowed
                      ? "border border-emerald-400/40 bg-emerald-400/15 text-emerald-300"
                      : "border border-red-400/30 bg-red-400/10 text-red-300/80",
                  )}
                >
                  {allowed ? "✓" : "✗"}
                </motion.div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </DocVisual>
  );
}

// ── OAuth handshake ──────────────────────────────────────────────

export function OAuthFlowVisual() {
  const reduce = useReducedMotion();
  const steps = [
    { label: "Click Connect",        icon: KeyRound },
    { label: "Provider consent",     icon: Layers   },
    { label: "Token stored (AES)",   icon: ShieldCheck },
    { label: "Tools unlocked",       icon: Workflow },
  ];
  return (
    <DocVisual label="OAuth flow">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {steps.map((s, i) => (
          <motion.div
            key={s.label}
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="flex flex-col items-center gap-2 rounded-lg border border-border/60 bg-card p-3"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-400/15 text-emerald-300">
              <s.icon className="h-4 w-4" />
            </span>
            <span className="text-center text-[11px] font-medium">
              {s.label}
            </span>
            <span className="text-[10px] text-muted-foreground">
              step {i + 1}
            </span>
          </motion.div>
        ))}
      </div>
    </DocVisual>
  );
}

// ── REST request / response ──────────────────────────────────────

export function RestRequestVisual({ method = "POST", path = "/v1/tools/gmail.search/call" }: {
  method?: string;
  path?: string;
}) {
  return (
    <DocVisual label="REST call">
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-md border border-foreground/10 bg-zinc-950 px-3 py-2">
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-bold",
              method === "GET"    && "bg-emerald-400/20 text-emerald-300",
              method === "POST"   && "bg-indigo-400/20 text-indigo-300",
              method === "PATCH"  && "bg-amber-400/20 text-amber-300",
              method === "DELETE" && "bg-red-400/20 text-red-300",
            )}
          >
            {method}
          </span>
          <code className="flex-1 truncate font-mono text-[12px] text-zinc-200">
            {path}
          </code>
        </div>
        <ArrowRight className="mx-auto h-4 w-4 rotate-90 text-muted-foreground" />
        <div className="rounded-md border border-foreground/10 bg-zinc-950 px-3 py-2">
          <pre className="m-0 font-mono text-[11px] leading-relaxed text-zinc-200">
{`{
  "data": { "id": "msg_abc", "subject": "Q4 plan" },
  "error": null,
  "meta": { "request_id": "req_42" }
}`}
          </pre>
        </div>
      </div>
    </DocVisual>
  );
}

// ── Quickstart steps (inline mini-visuals) ───────────────────────

export function QuickstartStep1Visual() {
  return (
    <StepVisual>
      <div className="grid w-full max-w-xs grid-cols-2 gap-2">
        {[
          // The signin button uses the OAuth-provider logo (lives under
          // /provider-logos/v3/, not /logos/v3/).
          { slug: "google", name: "Google", src: "/provider-logos/v3/google.svg" },
          { slug: "github", name: "GitHub", src: "/provider-logos/v3/github.svg" },
        ].map((p) => (
          <div
            key={p.slug}
            className="flex items-center justify-center gap-2 rounded-md border border-border/60 bg-card px-3 py-2 text-[12px]"
          >
            <IntegrationLogo slug={p.slug} src={p.src} name={p.name} size={16} />
            Continue
          </div>
        ))}
        <div className="col-span-2 mt-1 rounded-md border border-dashed border-border/40 px-3 py-2 text-center text-[11px] text-muted-foreground">
          or email + password
        </div>
      </div>
    </StepVisual>
  );
}

export function QuickstartStep2Visual() {
  return (
    <StepVisual>
      <div className="w-full max-w-xs rounded-lg border border-border/60 bg-card p-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          New project
        </p>
        <div className="mb-2 flex items-center gap-2 rounded-md border border-foreground/15 bg-background px-2 py-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 via-fuchsia-500 to-teal-400 text-[10px] font-bold text-white">
            P
          </span>
          <code className="font-mono text-[11px]">personal-assistant</code>
        </div>
        <button
          type="button"
          className="w-full rounded-md bg-foreground py-1.5 text-[11px] font-semibold text-background"
        >
          Create
        </button>
      </div>
    </StepVisual>
  );
}

export function QuickstartStep3Visual() {
  const reduce = useReducedMotion();
  return (
    <StepVisual>
      <div className="flex w-full max-w-xs flex-col gap-2">
        <div className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-2 text-[12px]">
          <IntegrationLogo
            slug="google"
            src="/provider-logos/v3/google.svg"
            name="Google"
            size={16}
          />
          <span className="flex-1">Connect Google</span>
          <motion.span
            initial={reduce ? false : { scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="rounded bg-emerald-400/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300"
          >
            granted
          </motion.span>
        </div>
        <div className="rounded-md border border-foreground/10 bg-background/60 px-3 py-1.5 text-[10px] text-muted-foreground">
          scopes: drive.readonly · calendar.readonly · gmail.readonly
        </div>
      </div>
    </StepVisual>
  );
}

export function QuickstartStep4Visual() {
  return (
    <StepVisual>
      <McpUrlInline />
    </StepVisual>
  );
}

function McpUrlInline() {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex w-full max-w-xs items-center gap-2 rounded-md border border-foreground/10 bg-zinc-950 px-2.5 py-1.5">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
      <code className="flex-1 truncate font-mono text-[11px] text-zinc-200">
        mcp.memkernel.com/p/abc123
      </code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText("https://mcp.memkernel.com/p/abc123");
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1400);
        }}
        className="flex h-6 w-6 items-center justify-center rounded border border-zinc-700 text-zinc-300"
      >
        {copied ? (
          <Check className="h-3 w-3 text-emerald-400" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </button>
    </div>
  );
}

export function QuickstartStep5Visual() {
  return (
    <StepVisual>
      <div className="w-full max-w-xs overflow-hidden rounded-md border border-foreground/10 bg-zinc-950">
        <HighlightedCode
          lang="json"
          code={`{
  "mcpServers": {
    "memkernel": {
      "url": "https://mcp.memkernel.com/p/abc123"
    }
  }
}`}
        />
      </div>
    </StepVisual>
  );
}

// ── Code-block doc visual (highlighted) ──────────────────────────

export function CodeVisual({
  code,
  lang = "ts",
  label,
  filename,
}: {
  code: string;
  lang?: string;
  label?: string;
  filename?: string;
}) {
  return (
    <DocVisual label={label} className="bg-zinc-950 p-0 sm:p-0">
      <div className="overflow-hidden">
        {filename && (
          <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-red-500/70" />
            <span className="h-2 w-2 rounded-full bg-yellow-500/70" />
            <span className="h-2 w-2 rounded-full bg-green-500/70" />
            <span className="ml-2 font-mono text-[11px] text-zinc-500">
              {filename}
            </span>
          </div>
        )}
        <HighlightedCode code={code} lang={lang} />
      </div>
    </DocVisual>
  );
}

// ── Coming-soon banner (for forward-looking docs) ────────────────

export function ComingSoonVisual({ feature }: { feature: string }) {
  return (
    <DocVisual label="Roadmap" className="border-dashed">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="relative flex h-12 w-12 items-center justify-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-indigo-500/25" />
          <span className="absolute inset-0 rounded-full border border-foreground/20" />
          <Boxes className="relative h-6 w-6 text-foreground/70" />
        </div>
        <p className="font-semibold">{feature}</p>
        <p className="max-w-md text-sm text-muted-foreground">
          Landing in a future release. Follow the changelog or the GitHub
          milestones to see when it ships.
        </p>
      </div>
    </DocVisual>
  );
}
